import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Box, Tabs, Typography } from '@mui/material';
import Tab from '@mui/material/Tab';
import { styled, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { GET_LIST_REVIEWS_FOR_ENTITY } from '../../../../graphql/videos';
import { colorsPalette, MIDDLE_MARGIN, useIsWidthUp } from '../../../../theme';
import { Query, QueryGetListReviewsForEntityArgs, ReviewableType, Video } from '../../../../types';
import { dateToReviewFormat, pipe, updateField } from '../../../../utils/helpers';
import { useAuth } from '../../../../utils/hooks';
import TabPanel, { a11yProps } from '../../../AnalyticsPage/components/TabPanel/TabPanel';
import Expert from '../Expert';
import Overview from '../Overview/Overview';
import Pagination, { usePagination } from '../Pagination/Pagination';
import RatingShort from '../RatingShort/RatingShort';
import ReviewForm from '../ReviewForm/ReviewForm';
import ReviewMessage from '../ReviewMessage/ReviewMessage';
import Summary from '../Summary';
import VideoAccordion from '../VideoAccordion';
import { TabsEnum } from '../VideoTabsContainer/VideoTabsContainer';
import useStyles from './styles';

interface VideoTabsProps {
  video: Video;
  isXApiPage?: boolean;
  rating?: number;
  loading: boolean;
  videoWithUrl: Video[];
  tabs: any;
  overviewExists: boolean;
  summaryExists: boolean;
}

const PAGE_STEP = 10;

const formatStr = (first, last) => `${first || ''} ${last?.slice(0, 1) || ''}`.trim();
const getFullName = (obj) => formatStr(obj?.learner?.first_name, obj?.learner?.last_name);
const formatCreatedAt = (obj) => dateToReviewFormat(obj?.created_at);
const updateCreatedAt = updateField('created_at', formatCreatedAt);
const updateFullName = updateField('full_name', getFullName);
export const updateReview = pipe(updateCreatedAt, updateFullName);

const VideoTabs: React.FC<VideoTabsProps> = ({
  video,
  isXApiPage = false,
  rating,
  videoWithUrl,
  loading,
  tabs,
  overviewExists,
  summaryExists,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { isAuthenticated } = useAuth();
  const isMobile = !useIsWidthUp('md');
  const { currentItem, handlePrev, handleNext, setTotalItems, currentPage } = usePagination(
    PAGE_STEP,
  );

  const showRating = isAuthenticated && !isXApiPage;

  const { data: reviewsList } = useQuery<
    Pick<Query, 'getListReviewsForEntity'>,
    QueryGetListReviewsForEntityArgs
  >(GET_LIST_REVIEWS_FOR_ENTITY, {
    variables: {
      entity_type: ReviewableType.Video,
      entity_id: Number(video?.id),
      pagination_option: {
        page: currentPage,
        count_items_on_page: PAGE_STEP,
      },
    },
    skip: !video?.id || !isAuthenticated || isXApiPage,
  });

  const totalReviews = reviewsList?.getListReviewsForEntity?.count_reviews || 0;

  useEffect(() => {
    if (totalReviews) {
      setTotalItems(totalReviews);
    }
  }, [setTotalItems, totalReviews]);

  const updatedReviews = useMemo(
    () => reviewsList?.getListReviewsForEntity?.reviews?.map(updateReview),
    [reviewsList],
  );

  const [tabIndex, setTabIndex] = useState(0);

  const handleChangeIndex = useCallback((event, newValue) => {
    setTabIndex(newValue);
  }, []);

  const handleChangeAccordion = useCallback(
    (panel) => (event, isExpanded) => {
      setTabIndex(isExpanded ? panel : -1);
    },
    [],
  );

  return (
    <Box className={classes.wrapper} data-testid="videoTabs">
      {isMobile ? (
        <>
          {overviewExists && (
            <VideoAccordion
              expanded={tabIndex === tabs[TabsEnum.Overview]}
              onChange={handleChangeAccordion(tabs[TabsEnum.Overview])}
            >
              <Typography className={classes.mobileTab}>{t('common.overview')}</Typography>
              <Overview
                keyLearningObjectives={video.key_learning_objectives}
                summary={video.executive_summary}
              />
            </VideoAccordion>
          )}
          {summaryExists && (
            <VideoAccordion
              expanded={tabIndex === tabs[TabsEnum.Summary]}
              onChange={handleChangeAccordion(tabs[TabsEnum.Summary])}
            >
              <Typography className={classes.mobileTab}>{t('common.summary')}</Typography>
              <Summary summary={video.summary} />
            </VideoAccordion>
          )}
          <VideoAccordion
            expanded={tabIndex === tabs[TabsEnum.Expert]}
            onChange={handleChangeAccordion(tabs[TabsEnum.Expert])}
          >
            <Typography className={classes.mobileTab}>{t('common.expert')}</Typography>
            <Expert
              expert={video.user}
              videos={videoWithUrl}
              loadingVideos={loading}
              isXApiPage={isXApiPage}
            />
          </VideoAccordion>
          {showRating && (
            <VideoAccordion
              expanded={tabIndex === tabs[TabsEnum.Reviews]}
              onChange={handleChangeAccordion(tabs[TabsEnum.Reviews])}
            >
              <RatingTitle className={classes.mobileTab}>
                {t('common.rating')}
                <RatingShort value={rating} />
              </RatingTitle>
              <Col>
                <ReviewForm id={video?.id} />
                <div>
                  {updatedReviews?.map((review, index) => (
                    <ReviewMessage
                      key={index}
                      rating={review?.stars_rating}
                      date={review?.created_at}
                      name={review?.full_name}
                      text={review?.comment}
                    />
                  ))}
                </div>
                {Boolean(totalReviews > 0) && (
                  <>
                    <Pagination
                      current={currentItem}
                      total={totalReviews}
                      step={PAGE_STEP}
                      handlePrev={handlePrev}
                      handleNext={handleNext}
                    />
                    <Divider />
                  </>
                )}
              </Col>
            </VideoAccordion>
          )}
        </>
      ) : (
        <>
          <Tabs
            value={tabIndex}
            onChange={handleChangeIndex}
            textColor="inherit"
            aria-label="video info tabs"
            classes={{
              root: classes.root,
              indicator: classes.indicator,
            }}
          >
            {overviewExists && (
              <Tab
                label={t('common.overview')}
                {...a11yProps(tabs[TabsEnum.Overview])}
                data-testid="overviewTab"
              />
            )}
            {summaryExists && (
              <Tab
                label={t('common.summary')}
                {...a11yProps(tabs[TabsEnum.Summary])}
                data-testid="summaryTab"
              />
            )}
            <Tab
              label={t('common.expert')}
              {...a11yProps(tabs[TabsEnum.Expert])}
              data-testid="expertTab"
            />
            {showRating && (
              <Tab
                label={t('common.reviews')}
                {...a11yProps(tabs[TabsEnum.Reviews])}
                data-testid="ratingTab"
              />
            )}
          </Tabs>
          {overviewExists && (
            <TabPanel value={tabIndex} index={tabs[TabsEnum.Overview]}>
              <Overview
                keyLearningObjectives={video.key_learning_objectives}
                summary={video.executive_summary}
              />
            </TabPanel>
          )}
          {summaryExists && (
            <TabPanel value={tabIndex} index={tabs[TabsEnum.Summary]}>
              <Summary summary={video.summary} />
            </TabPanel>
          )}
          {showRating && (
            <TabPanel value={tabIndex} index={tabs[TabsEnum.Reviews]} data-testid="reviewTab">
              <ReviewForm id={video?.id} />
              <div>
                {updatedReviews?.map((review, index) => (
                  <ReviewMessage
                    key={index}
                    rating={review?.stars_rating}
                    date={review?.created_at}
                    name={review?.full_name}
                    text={review?.comment}
                  />
                ))}
              </div>
              {Boolean(totalReviews > 0) && (
                <>
                  <Pagination
                    current={currentItem}
                    total={totalReviews}
                    step={PAGE_STEP}
                    handlePrev={handlePrev}
                    handleNext={handleNext}
                  />
                  <Divider />
                </>
              )}
            </TabPanel>
          )}
          <TabPanel value={tabIndex} index={tabs[TabsEnum.Expert]}>
            <Expert
              expert={video.user}
              videos={videoWithUrl}
              loadingVideos={loading}
              isXApiPage={isXApiPage}
            />
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default VideoTabs;

const RatingTitle = styled(Typography)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexGrow: 1,
});
const Col = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});
const Divider = styled('div')(({ theme }: { theme: Theme }) => ({
  height: 1,
  width: '100%',
  marginTop: MIDDLE_MARGIN,
  background: colorsPalette.violetAnalyticsCard,
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));
